import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Textarea                                                          */
/* ------------------------------------------------------------------ */

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  /** Maximum character count. When set, displays a counter. */
  maxLength?: number;
  /** Show character count even without maxLength. */
  showCount?: boolean;
}

const css = /* css */ `
  .sf-textarea {
    display: block;
    width: 100%;
    border: 1px solid var(--sf-border);
    border-radius: var(--sf-radius-md);
    background: var(--sf-bg-elevated);
    color: var(--sf-text);
    font-family: var(--sf-font-sans);
    font-size: var(--sf-text-base);
    padding: 8px 12px;
    min-height: 80px;
    resize: vertical;
    transition: border-color var(--sf-transition-fast),
                box-shadow var(--sf-transition-fast);
    line-height: var(--sf-leading-normal);
  }

  .sf-textarea::placeholder { color: var(--sf-text-muted); }
  .sf-textarea:hover { border-color: var(--sf-border-strong); }

  .sf-textarea:focus-visible {
    outline: var(--sf-ring-width) solid var(--sf-ring-color);
    outline-offset: var(--sf-ring-offset);
    border-color: var(--sf-border-focus);
  }

  .sf-textarea:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: var(--sf-bg-inset);
  }

  .sf-textarea--error { border-color: var(--sf-error); }
  .sf-textarea--error:focus-visible {
    outline-color: rgba(201, 42, 42, 0.4);
    border-color: var(--sf-error);
  }

  .sf-textarea-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--sf-space-2);
  }

  .sf-textarea-count {
    font-size: var(--sf-text-xs);
    color: var(--sf-text-muted);
    margin-left: auto;
    flex-shrink: 0;
  }

  .sf-textarea-count--over {
    color: var(--sf-error);
    font-weight: var(--sf-weight-medium);
  }
`;

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      hint,
      error,
      maxLength,
      showCount = false,
      className,
      id: idProp,
      value,
      defaultValue,
      onChange,
      ...rest
    },
    ref,
  ) => {
    injectStyles("sf-textarea", css);

    const autoId = React.useId();
    const id = idProp ?? autoId;
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;

    const [internalValue, setInternalValue] = React.useState(
      (defaultValue as string) ?? "",
    );
    const controlled = value !== undefined;
    const currentValue = controlled ? String(value) : internalValue;
    const charCount = currentValue.length;
    const isOver = maxLength != null && charCount > maxLength;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!controlled) setInternalValue(e.target.value);
      onChange?.(e);
    };

    const displayCount = showCount || maxLength != null;

    const textareaClasses = [
      "sf-textarea",
      error ? "sf-textarea--error" : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="sf-field">
        {label && (
          <label className="sf-field__label" htmlFor={id}>
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={id}
          className={textareaClasses}
          value={controlled ? value : undefined}
          defaultValue={controlled ? undefined : defaultValue}
          onChange={handleChange}
          maxLength={undefined /* we handle count ourselves */}
          aria-invalid={error || isOver ? true : undefined}
          aria-describedby={
            [hintId, errorId].filter(Boolean).join(" ") || undefined
          }
          {...rest}
        />
        <div className="sf-textarea-footer">
          <div>
            {hint && !error && (
              <span id={hintId} className="sf-field__hint">
                {hint}
              </span>
            )}
            {error && (
              <span id={errorId} className="sf-field__error" role="alert">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 3.75a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zM8 11a1 1 0 100 2 1 1 0 000-2z" />
                </svg>
                {error}
              </span>
            )}
          </div>
          {displayCount && (
            <span
              className={`sf-textarea-count${isOver ? " sf-textarea-count--over" : ""}`}
              aria-live="polite"
            >
              {charCount}
              {maxLength != null ? `/${maxLength}` : ""}
            </span>
          )}
        </div>
      </div>
    );
  },
);

Textarea.displayName = "Textarea";
export { Textarea };
